/* eslint-disable no-param-reassign */
import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { StoreThunkApiConfig } from "~/store-config.ts";
import type { State as ReferenceState } from "~/features/reference/state.ts";
import type { State as UserState } from "~/features/user/store-slice.ts";
import { setWhoFor, clearGiftType } from "~/features/user/store-slice.ts";
import { loadGiftVariantDetails } from "~/features/reference/store-slice.ts";
import type { GenericSize } from "@brickme/project-core/src/model/geom.ts";

type GiftRedemptionStep =
	| { type: "showCongratulations" }
	| { type: "showName" }
	| {
			type: "configuring";
			readonly giftFramingVariantId: string | undefined;
			readonly giftFramingVariantQty: number | undefined;
	  }
	| { type: "error"; message: string };

type GiftRedemptionState = {
	readonly sessionId: string;
	readonly variantId: string;
	readonly giftSize: GenericSize<number>;
	readonly giftBaseplateSize: number;
	readonly giftProductTitle: string;
	readonly giftFramingVariantId: string | undefined;
	readonly giftFramingVariantQty: number | undefined;
	readonly giftFramingType: "nano-strips" | "frame" | undefined;
	readonly originalGiftFramingVariantId: string | undefined;
	readonly originalGiftFramingVariantQty: number | undefined;
	readonly step: GiftRedemptionStep;
};

type State = {
	readonly giftRedemption?: GiftRedemptionState;
};

const initialState: State = {
	giftRedemption: undefined,
};

const name = "gift";

const loadGiftRedemption = createAsyncThunk<
	void,
	{
		sessionId: string;
		variantId: string;
		framingVariantId?: string;
		framingVariantQty?: number;
	},
	StoreThunkApiConfig<{
		gift: State;
		reference: ReferenceState;
		user: UserState;
	}>
>(
	`${name}/loadGiftRedemption`,
	async (
		{ sessionId, variantId, framingVariantId, framingVariantQty },
		{ dispatch, getState },
	) => {
		const {
			reference: { defaultCountryCode },
		} = getState();

		// Load variant details to get size and price
		const variantResult = await dispatch(
			loadGiftVariantDetails({
				sessionId,
				variantId,
				framingVariantId,
				framingVariantQty,
				countryCode: defaultCountryCode,
			}),
		);

		// Check if the action was fulfilled successfully
		if (loadGiftVariantDetails.fulfilled.match(variantResult)) {
			const variantDetails = variantResult.payload;

			// Set gift redemption state
			dispatch(
				setGiftRedemption({
					sessionId,
					variantId,
					giftSize: variantDetails.giftSize,
					giftBaseplateSize: variantDetails.giftBaseplateSize,
					giftProductTitle: variantDetails.giftProductTitle,
					giftFramingVariantId: variantDetails.giftFramingVariantId,
					giftFramingVariantQty: variantDetails.giftFramingVariantQty,
					giftFramingType: variantDetails.giftFramingType,
					step: { type: "showCongratulations" },
				}),
			);

			// Set user preferences for gift redemption
			dispatch(setWhoFor("myself"));
			dispatch(clearGiftType());

			return;
		} else {
			// Handle rejected thunk with user-friendly messages
			const errorMessage = variantResult.error?.message || "Unknown error";
			let userFriendlyMessage: string;

			if (errorMessage.includes("Node not found")) {
				userFriendlyMessage =
					"This gift link appears to be invalid or has expired. Please check the link and try again.";
			} else if (errorMessage.includes("Invalid product title format")) {
				userFriendlyMessage =
					"This gift product is not configured correctly. Please contact support for assistance.";
			} else if (
				errorMessage.includes("Could not find baseplate size option")
			) {
				userFriendlyMessage =
					"This gift product is not configured correctly. Please contact support for assistance.";
			} else if (errorMessage.includes("Invalid baseplate size format")) {
				userFriendlyMessage =
					"This gift product is not configured correctly. Please contact support for assistance.";
			} else if (errorMessage.includes("Unsupported baseplate size")) {
				userFriendlyMessage =
					"This gift product is not configured correctly. Please contact support for assistance.";
			} else {
				userFriendlyMessage =
					"Unable to load your gift. Please try again or contact support if the problem persists.";
			}

			// Set error state with step
			dispatch(
				setGiftRedemption({
					sessionId,
					variantId,
					giftSize: { width: 0, height: 0 },
					giftBaseplateSize: 0,
					giftProductTitle: "Error",
					giftFramingVariantId: undefined,
					giftFramingVariantQty: undefined,
					giftFramingType: undefined,
					step: { type: "error", message: userFriendlyMessage },
				}),
			);

			return;
		}
	},
);

const slice = createSlice({
	name,
	initialState,
	reducers: {
		setGiftRedemption(
			state,
			action: PayloadAction<
				Omit<
					GiftRedemptionState,
					"originalGiftFramingVariantId" | "originalGiftFramingVariantQty"
				>
			>,
		) {
			state.giftRedemption = {
				sessionId: action.payload.sessionId,
				variantId: action.payload.variantId,
				giftSize: action.payload.giftSize,
				giftBaseplateSize: action.payload.giftBaseplateSize,
				giftProductTitle: action.payload.giftProductTitle,
				giftFramingVariantId: action.payload.giftFramingVariantId,
				giftFramingVariantQty: action.payload.giftFramingVariantQty,
				giftFramingType: action.payload.giftFramingType,
				originalGiftFramingVariantId: action.payload.giftFramingVariantId,
				originalGiftFramingVariantQty: action.payload.giftFramingVariantQty,
				step: action.payload.step,
			};
		},
		setGiftRedemptionStep(state, action: PayloadAction<GiftRedemptionStep>) {
			if (state.giftRedemption) {
				state.giftRedemption.step = action.payload;
			}
		},
		updateGiftRedemptionFramingDetails(
			state,
			action: PayloadAction<{
				giftFramingVariantId?: string | undefined;
				giftFramingVariantQty?: number | undefined;
			}>,
		) {
			if (state.giftRedemption) {
				if ("giftFramingVariantId" in action.payload) {
					state.giftRedemption.giftFramingVariantId =
						action.payload.giftFramingVariantId;
				}
				if ("giftFramingVariantQty" in action.payload) {
					state.giftRedemption.giftFramingVariantQty =
						action.payload.giftFramingVariantQty;
				}
			}
		},
	},
});

export const {
	setGiftRedemption,
	updateGiftRedemptionFramingDetails,
	setGiftRedemptionStep,
} = slice.actions;

export { loadGiftRedemption };
export type { State, GiftRedemptionState, GiftRedemptionStep };
export default slice.reducer;
